import { HtmlHTMLAttributes, useState } from 'react';
import styled from 'styled-components';
import { keyframes } from 'styled-components';

import { ReactComponent as ExportIcon } from 'app/assets/icons/icon-export.svg';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const moveDown = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(2px); }
`;

const ButtonBase = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 100px;
  height: 46px;
  padding: 0 24px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;

  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }
`;

const Button = styled(ButtonBase)<{ color: string; linearEnd: string }>`
  background: white;
  color: ${({ color }) => color || '#7e8299'};
  border: 2px solid ${({ color }) => color || '#7e8299'};
  border-radius: 8px;
  /* box-shadow: 0 2px 10px rgba(13, 148, 136, 0.1); */
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to right,
      ${({ color }) => color || '#7e8299'},
      ${({ linearEnd }) => linearEnd || '#9ca3b7'}
    );
    opacity: 0;
    transition: all 0.3s ease;
    z-index: 0;
  }

  svg path {
    stroke: ${({ color }) => color};
  }

  &:hover:not(:disabled) {
    color: white;
    border-color: transparent;
    /* box-shadow: 0 4px 16px rgba(13, 148, 136, 0.25); */
    transform: translateY(-1px);
  }

  &:hover:not(:disabled)::before {
    opacity: 1;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:hover svg {
    transform: translateY(0);
    animation: ${moveDown} 0.5s ease;
    /* filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.5)); */
  }

  &:hover svg path {
    stroke: white;
  }
`;

const ButtonContent = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1;
`;

const Spinner = styled.svg`
  animation: ${spin} 1s linear infinite;
  height: 20px;
  width: 20px;
`;

interface ExportButtonProps extends HtmlHTMLAttributes<HTMLButtonElement> {
  text?: string;
  color?: string;
  linearEnd?: string;
  loadingText?: string;
  onButtonClick?: () => void;
}

const ExportButton = ({
  text,
  loadingText = 'Exporting...',
  onButtonClick,
  color,
  linearEnd,
  ...props
}: ExportButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleButtonClick = async () => {
    try {
      setIsLoading(true);
      if (onButtonClick) await onButtonClick();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      color={color || '#7e8299'}
      linearEnd={linearEnd || '#9ca3b7'}
      onClick={handleButtonClick}
      disabled={isLoading}
      {...props}
    >
      <ButtonContent>
        {isLoading ? (
          <>
            <Spinner viewBox="0 0 24 24">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                opacity="0.25"
              />
              <path
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                opacity="0.75"
              />
            </Spinner>
            {loadingText}
          </>
        ) : (
          <>
            <ExportIcon />
            {text}
          </>
        )}
      </ButtonContent>
    </Button>
  );
};

export default ExportButton;
